/**
 * @file index.tsx
 *
 * @fileoverview Renders related blogs teasers for a particular blog article.
 */
import { FormattedMessage } from 'react-intl';
import AnchorDiv from '~/jobiqo/components/AnchorDiv';
import Image from '~/jobiqo/components/Image';
import Link from '~/jobiqo/components/Link';
import { BlogPost } from '~/jobiqo/models/blog';
import { getCommonTranslations } from '~/jobiqo/utils/intl';
import { getReducedSummary } from '~/jobiqo/components/Blog/utils';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { WYSIWYGText } from '~/vcl/atoms/13-text/02-WYSIWYG';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { BlogTeaserPlaceholder } from '~/vcl/organisms/05-placeholders/08-blog/01-teaser';
import { BlogTeaser } from '~/vcl/organisms/11-blog/01-blog-teaser';

export interface BlogRealtedBlogsProps {
  /**
   * The related blogs.
   */
  relatedBlogs: BlogPost[];
  /**
   * The data loading indication.
   */
  loading: boolean;
}
export default function BlogRealtedBlogs({
  relatedBlogs,
  loading,
}: BlogRealtedBlogsProps) {
  const { ADVERTORIAL_LABEL } = getCommonTranslations();

  return relatedBlogs.length === 0 ? null : (
    <>
      <Text tag="h2" fontSize={6}>
        <FormattedMessage
          id="blog_related_blogs_title"
          defaultMessage="Related blogs"
        />
      </Text>
      <Grid columns={[1, 2, 3]} marginTop={6} marginBottom={11}>
        {loading
          ? new Array(3).fill(BlogTeaserPlaceholder).map((Component, index) => (
              <Cell key={index}>
                <Component />
              </Cell>
            ))
          : relatedBlogs.map(
              ({
                image,
                title,
                category,
                url,
                summary,
                featured,
              }: BlogPost) => (
                <Cell key={title} display="grid">
                  <AnchorDiv route={url.path}>
                    <BlogTeaser
                      {...(image?.url && {
                        banner: (
                          <Image
                            src={image.url}
                            height={300}
                            width={600}
                            alt={image.alt ?? ''}
                            {...(image.title && { title: image.title })}
                          />
                        ),
                      })}
                      title={
                        <Link href={url.path} target="_blank">
                          {title}
                        </Link>
                      }
                      content={
                        summary ? (
                          <WYSIWYGText
                            format="full_html"
                            content={getReducedSummary(summary)}
                          />
                        ) : (
                          ''
                        )
                      }
                      {...(category && { categories: [category.label] })}
                      actions={
                        <Link href={url.path} target="_blank">
                          <FormattedMessage
                            id="blog_see_more"
                            defaultMessage="See more"
                          />
                        </Link>
                      }
                      {...(featured && {
                        advertorialLabel: ADVERTORIAL_LABEL,
                      })}
                    />
                  </AnchorDiv>
                </Cell>
              ),
            )}
      </Grid>
    </>
  );
}
export { BlogRealtedBlogs };
